import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import OrderForm from '../forms/OrderForm';
import { PencilSquareIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import Departamentos from '../../../src/assets/data/departamentos.json';
import Ciudades from '../../../src/assets/data/ciudades.json';
import {tiposDeDocumento, formaPago, metodoPago, plazosPago} from '../../assets/data/arrays';
import LoadingSpinner from '../LoadingSpinner';
import OfimaRemisionWithConsecutivos from '../OfimaRemisionWithConsecutivos';
import type { OfimaConsecutivos } from '../../types/order';

interface OrderDetail {
  id: number;
  orders_id: number;
  origin_order_id: number;
  cotizacion_id?: number;
  store_name: string;
  status: string;
  total: string;
  guia?: string;
  date_created: string;
  date_modified: string;
  id_remision?: string;
  ofima_consecutivos?: OfimaConsecutivos | null;
  agente?: number;
  customer: {
    first_name: string;
    last_name: string;
    email: string;
    document?: string;
    tipo_documento?: string;
    tipo_persona?: string;
    idCuenta?: number;
    idReteFuente?: string;
    idReteICA?: string;
    PctReteIVA?: string;
  };
  billingAddress: {
    address_1: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    phone?: string;
  };
  shippingAddress: {
    address_1: string;
    city: string;
    state: string;
    country: string;
    postcode: string;
    id_cliente?: string;
    phone?: string;
  };
  lineItems: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    price: string;
    iva: string;
  }>;
  extraDetails?: {
    servicio_utilizado?: string;
    tipo_lead?: string;
    tipo_cliente?: string;
    transportadora?: string;
    ciudad_origen?: string;
    ciudad_destino?: string;
    banco?: string;
    conocer?: string;
    id_pago?: string;
    fuente_atribucion?: string;
    marca?: string;
    nom_remitente?: string;
    cod_regional?: string;
    cod_oficina?: string;
    cod_cuenta?: string;
    forma_pago?: string;
    dir_remitente?: string;
    tel_remitente?: string;
    ced_remitente?: string;
    currency?: string;
    payment_method?: string;
    trpcol_numguia?: string;
    customer_note?: string;
    transaction_id?: string;
  };
}

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [orderDetail, setOrderDetail] = React.useState<OrderDetail | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    const fetchOrderDetail = async () => {
      try {
        const response = await axiosInstance.get(`/list/${id}`);
        const data = response.data;
        setOrderDetail(data);
      } catch (error) {
        console.error('Error obteniendo el detalle del pedido:', error);
      }
    };

    fetchOrderDetail();
  }, [id]);

  const handleStatusChange = async () => {
    setLoading(true);
    setError(null);
    console.log('id de la orden para cambio de estado:::',{OrderId: orderDetail?.orders_id})
    try {
      await axiosInstance.post(`/falabella/change-status`, { OrderId: orderDetail?.orders_id });
      alert("Estado cambiado exitosamente 🚀");
    } catch (error: any) {
      setError("Error cambiando el estado");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    try {
      await axiosInstance.put(`/update/${id}`, data);
      alert("Pedido actualizado exitosamente 🚀");
    } catch (error) {
      console.error('Error actualizando el pedido:', error);
      alert("Error actualizando el pedido");
    }
  };

  if (!orderDetail) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="p-10 bg-gray-50 min-h-screen mx-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              const from = location.state?.from;
              console.log('🔙 Regresando a URL:', from || '/orders');
              if (from) {
                console.log('🔙 Navegando a:', from);
                navigate(from);
              } else {
                console.log('🔙 Navegando a /orders (fallback)');
                navigate('/orders');
              }
            }} 
            className="text-blue-800 hover:text-blue-900 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-blue-800">
              Detalles del Pedido #{orderDetail.orders_id ? orderDetail.orders_id: orderDetail.id}
            </h1>
            <p className="text-sm text-gray-600">
              Última actualización: {new Date(orderDetail.date_modified).toLocaleString()}
            </p>
            <p className="text-sm text-gray-600">
            {orderDetail.cotizacion_id !== undefined && orderDetail.cotizacion_id > 0 && (
            <button onClick={() => navigate(`/cotizaciones/${orderDetail.cotizacion_id}`)} className="text-blue-800 hover:text-blue-900 transition-colors"> 
               Ver cotizacion de origen #<b className="underline font-semibold">{orderDetail.cotizacion_id}</b>
            </button>
            )}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:gap-4">
          <OfimaRemisionWithConsecutivos
            idRemision={orderDetail.id_remision}
            ofimaConsecutivos={orderDetail.ofima_consecutivos ?? null}
          />
          {orderDetail.store_name === 'Falabella' && (
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => navigate(`/orders/${id}/update`)}
                className="bg-custom-blue text-white px-4 py-2 rounded-md hover:bg-blue-900 transition-colors flex items-center gap-2"
              >
                <PencilSquareIcon className="h-5 w-5" />
                Editar
              </button>
              <button
                onClick={handleStatusChange}
                className="bg-custom-blue text-white px-4 py-2 rounded-md hover:bg-blue-900 transition-colors flex items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cambiando...
                  </>
                ) : ('Cambiar a asignado')}
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <OrderForm 
        initialData={orderDetail}
        mode="view"
        onSubmit={handleSubmit}
        tiposDeDocumento={tiposDeDocumento}
        departamentos={Departamentos}
        ciudades={Ciudades}
        formaPago={formaPago}
        metodoPago={metodoPago}
        plazosPago={plazosPago}
      />
    </div>
  );
};

export default OrderDetails;